import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { connect } from 'react-redux';
import { sortBy, uniqBy } from 'lodash';

import { selectors as arSchedulesSelectors } from '../../../../state/modules/availability-requirement-schedules';
import { selectors as arSelectors } from '../../../../state/modules/availability-requirements';
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as skillSelectors } from '../../../../state/modules/skills';
import { selectors as dashboardSelectors } from '../../../../state/modules/dashboards';
import { selectors as availabilityCodeSelectors } from '../../../../state/modules/availability-codes';
import { groupSchedulePath } from '../../../../utils/paths';
import { linkToNewGroupDutyTransaction } from '../../../../utils/dutyTransactions';

import { Row, Dropdown, PortalOrigin } from '../../../ui-kit';
import { IconCalendar, IconCalendarPlus } from '../../../ui-kit/icons';
import Loader from '../../../general/Loader';
import Settings, {
  WARNING_LEVEL_SHOW,
  WARNING_LEVEL_HIDE,
  WARNING_LEVEL_EXPAND,
  upgradeWarningLevelSettings
} from '../CrewLevelWarnings/Settings';
import AvailabilityRequirementCard from './AvailabilityRequirementCard';
import AccessRightsContext from '../../../contexts/AccessRightsContext';
import moment from 'moment';

const convertWarningLevelDisplaySetting = setting => ({
  show: setting !== WARNING_LEVEL_HIDE,
  expand: setting === WARNING_LEVEL_EXPAND
});

const convertWarningLevelDisplaySettings = ({
  show_above_buffer,
  show_on_buffer,
  show_on_required,
  show_below_required
}) => ({
  above_buffer: convertWarningLevelDisplaySetting(show_above_buffer),
  on_buffer: convertWarningLevelDisplaySetting(show_on_buffer),
  on_required: convertWarningLevelDisplaySetting(show_on_required),
  below_required: convertWarningLevelDisplaySetting(show_below_required),
  below_buffer: convertWarningLevelDisplaySetting(show_on_buffer)
});

const CrewLevelWarnings = ({
  show_above_buffer,
  show_on_buffer,
  show_on_required,
  show_below_required,
  show_below_buffer,
  dashboardAPIManager,
  accessScopeGroups,
  visible_availability_requirements,
  ...props
}) => {
  const accessRights = useContext(AccessRightsContext);

  const [menuState, setMenuState] = useState({
    show: false,
    x: 0,
    y: 0,
    groupIds: [],
    startTime: null,
    endTime: null
  });

  const handleContextMenu = useCallback((event, params) => {
    event.preventDefault();
    setMenuState({
      show: true,
      x: event.pageX,
      y: event.pageY,
      ...params
    });
  }, []);

  const minEndTime = useMemo(() => moment(props.dashboardDateTime), [props.dashboardDateTime]);
  const maxStartTime = useMemo(() => minEndTime.clone().add(5, 'days'), [minEndTime]);

  useEffect(() => {
    dashboardAPIManager.fetchGroupAndSubgroups();
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchAvailabilityCodes();

    dashboardAPIManager.registerForAvailabilityRequirementScheduleUpdates({
      look_ahead: 432_000
    }); //5 days expressed in seconds
  }, [dashboardAPIManager]);

  useEffect(() => {
    // To close the context menu when clicking outside of it
    document.addEventListener('click', function (e) {
      const inside = e.target.closest('.crew-level-warnings-context-menu');
      if (!inside) {
        setMenuState({ ...menuState, show: false });
      }
    });
  }, []);

  const filteredAvailabilityRequirements = useMemo(() => {
    return sortBy(
      accessScopeGroups
        .flatMap(g => g.availability_requirements || [])
        .filter(
          ar =>
            !visible_availability_requirements ||
            visible_availability_requirements.length === 0 ||
            visible_availability_requirements.includes(ar.id)
        ),
      'display_order'
    );
  }, [accessScopeGroups, visible_availability_requirements]);

  const groupOptions = useMemo(
    () =>
      uniqBy(
        menuState.groupIds
          .map(gid => props.groupsById[gid])
          .filter(Boolean)
          .filter(g => g.type === 'station'),
        'id'
      ),
    [menuState.groupIds, props.groupsById]
  );

  let maxCols = { 1: 1, 2: 1, 3: 2, 4: 3, 5: 3, 6: 4 }[props.width];
  maxCols = Math.min(filteredAvailabilityRequirements.length, maxCols);

  // translate the show/hide/expand settings to a more readable format
  const filters = useMemo(
    () =>
      convertWarningLevelDisplaySettings(
        upgradeWarningLevelSettings({
          show_above_buffer,
          show_on_buffer,
          show_on_required,
          show_below_required,
          show_below_buffer
        })
      ),
    [show_above_buffer, show_on_buffer, show_on_required, show_below_required, show_below_buffer]
  );

  if (props.availiablityRequirementSchedulesLoading) return <Loader />;

  return (
    <Row xs={1} md={Math.min(maxCols, 3)} lg={Math.min(maxCols, 4)} xl={Math.min(maxCols, 6)} className="g-2">
      {/* use portal to render menu outside of the widget view to avoid positioning issues */}
      <PortalOrigin>
        <Dropdown.ContextMenu
          x={menuState.x}
          y={menuState.y}
          show={menuState.show}
          className={'crew-level-warnings-context-menu'}
          id={'crew-level-warnings-context-menu'}
        >
          {groupOptions.map(group => (
            <Dropdown.Item
              key={group.id}
              href={groupSchedulePath(group.id, menuState.startTime, menuState.endTime)}
              target="_blank"
            >
              <IconCalendar /> {I18n.t('schedules.group_schedule_overview')} {group.name}
            </Dropdown.Item>
          ))}
          {accessRights.create.includes('duty_request') &&
            groupOptions.map(group => (
              <Dropdown.Item
                key={group.id}
                href={linkToNewGroupDutyTransaction('duty_requests', group.id, {
                  start_time: menuState.startTime,
                  end_time: menuState.endTime
                })}
                target="_blank"
              >
                <IconCalendarPlus /> {I18n.t('activerecord.models.schedule/exchanges/duty_request.one')} {group.name}
              </Dropdown.Item>
            ))}
        </Dropdown.ContextMenu>
      </PortalOrigin>
      {filteredAvailabilityRequirements.map(requirement => (
        <AvailabilityRequirementCard
          key={requirement.id}
          schedule={props.availabilityRequirementSchedules[requirement.id]}
          skillsById={props.skillsById}
          filters={filters}
          maxStartTime={maxStartTime}
          minEndTime={minEndTime}
          handleContextMenu={handleContextMenu}
          showNamesWhenOrangeOrRed={props.show_names_when_orange_or_red}
        />
      ))}
    </Row>
  );
};

Object.assign(CrewLevelWarnings, {
  SettingsContent: Settings,
  defaultSettings: {
    show_below_required: WARNING_LEVEL_EXPAND,
    show_on_required: WARNING_LEVEL_SHOW,
    show_on_buffer: WARNING_LEVEL_SHOW,
    show_above_buffer: WARNING_LEVEL_SHOW,
    show_below_buffer: WARNING_LEVEL_SHOW,
    show_all_requirements: false
  }
});

const mapStateToProps = state => ({
  availabilityRequirementSchedules: arSchedulesSelectors.selectFullSchedulesState(state),
  availiablityRequirementSchedulesLoading: arSchedulesSelectors.loading(state),
  currentAvailabilityRequirementSchedule: arSchedulesSelectors.selectDashboardState(state),
  skillsById: skillSelectors.byId(state),
  groupsById: groupSelectors.byId(state),
  availabilityCodesById: availabilityCodeSelectors.byId(state),
  skills: state.skills,
  users: state.users,
  availabilityCodes: state.availabilityCodes,
  dashboardDateTime: state.dashboards.dashboardDatetime.dateTime,
  hiddenAvailabilityRequirementIds: arSelectors.hiddenAvailabilityRequirementIds(state),
  availabilityRequirementsPerGroup: arSelectors.availabilityRequirementsPerGroup(state),
  accessScopeGroups: dashboardSelectors.selectCurrentDashboardAccessScopeGroups(state)
});

export default connect(mapStateToProps)(CrewLevelWarnings);
